import { WEBUI_API_V2_BASE_URL } from '$lib/constants';
import { fetchWithAuth } from '../..';

export interface FileMetadata {
	name: string;
	description?: string;
	content_type?: string;
	size?: number;
	source?: string;
	loader?: string;
	pages?: Array<{
		page_number: number;
		content: string;
		metadata?: Record<string, any>;
		has_images?: boolean;
		has_tables?: boolean;
	}>;
}

export enum ItemType {
	FILE = 'file',
	JSON_OBJECT = 'json_object',
	TEXT = 'text',
	TOPDESK_KNOWLEDGE_ITEM = 'topdesk_knowledge_item',
	TOPDESK_INCIDENT = 'topdesk_incident',
	IMAGE = 'image',
	YOUTUBE = 'youtube'
}

export interface GenericItem {
	id: string;
	name: string;
	content: string | null;
	content_type: string;
	item_type: string;
	meta: {
		source: string;
		loader: string;
	};
	knowledge_id: string;
	user_id: string;
	status: string;
	file_path: string;
	file_size: number;
	chunk_count: number;
	created_at: number;
	updated_at: number;
	dense_embeddings: any | null;
	sparse_embeddings: any | null;

	// Legacy fields for backward compatibility
	type?: ItemType;
	file?: string;
	hasExtractedContent?: boolean;
	url?: string;
	username?: string;
	collection?: {
		name: string;
		description: string;
	};
	description?: string;
	blob?: any;
	data?: {
		content?: string;
	};
	// Client-side fields for tracking upload/processing status
	statusMessage?: string;
	createdAt?: number;
	updatedAt?: number;
	// Temporary identifier used during upload process
	itemId?: string;
}

// Backward-compatibility alias used across workspace components
export type FileResponse = GenericItem;

export enum ItemStatus {
	CREATED = 'CREATED',
	PENDING = 'PENDING',
	PROCESSING = 'PROCESSING',
	PROCESSED = 'PROCESSED',
	ERROR = 'ERROR'
}

/**
 * Create a YouTube item for transcription
 */
export const createYouTubeItem = async (
	knowledgeId: string,
	content: string,
	itemType: string = 'text'
): Promise<GenericItem> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/process-youtube`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				knowledge_id: knowledgeId,
				content: content,
				content_type: 'audio/youtube',
				item_type: itemType
			})
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to create YouTube item';
	}

	return res.json();
};

/**
 * Upload and process a file for a knowledge base
 */
export const uploadFile = async (knowledgeId: string, file: File): Promise<GenericItem> => {
	const formData = new FormData();
	formData.append('file', file);

	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/process`,
		{
			method: 'POST',
			body: formData
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to upload file';
	}

	return res.json();
};

/**
 * List all files in a knowledge base
 */
export const listItems = async (
	knowledgeId: string,
	includeContent: boolean = false,
	skip: number = 0,
	limit: number = 100,
	search?: string
): Promise<GenericItem[]> => {
	const params = new URLSearchParams({
		include_content: String(includeContent),
		skip: String(skip),
		limit: String(limit)
	});

	if (search) {
		params.append('search', search);
	}

	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items?${params.toString()}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to list files';
	}

	return res.json();
};

/**
 * Alias for listItems for backward compatibility
 */
export const listFiles = async (
	knowledgeId: string,
	includeContent: boolean = false,
	skip: number = 0,
	limit: number = 100,
	search?: string
): Promise<GenericItem[]> => {
	return listItems(knowledgeId, includeContent, skip, limit, search);
};

/**
 * Get a specific file by ID
 */
export const getFile = async (knowledgeId: string, fileId: string): Promise<GenericItem> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${fileId}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to get file';
	}

	return res.json();
};

/**
 * Get item content with full metadata
 */
export const getItemContent = async (knowledgeId: string, itemId: string): Promise<string> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}/content`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to get item content';
	}

	return await res.text();
};

/**
 * Delete a file from a knowledge base
 */
export const deleteFile = async (
	knowledgeId: string,
	fileId: string
): Promise<{ message: string }> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${fileId}`,
		{
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json();
		throw error.detail || 'Failed to delete file';
	}

	return res.json();
};

/**
 * Get a specific item by ID with complete data (content and metadata)
 *
 * This combines functionality of getFile and getItemContent to provide a complete
 * item object including its content and all metadata in a single API call.
 */
export const getItem = async (knowledgeId: string, itemId: string): Promise<GenericItem> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}`,
		{
			method: 'GET',
			headers: {
				Accept: 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to get item' }));
		throw error.detail || 'Failed to get item';
	}

	return res.json();
};

/**
 * Download a file by its ID
 */
export const getItemContentById = async (itemId: string, knowledgeId: string): Promise<Blob> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}/download`,
		{
			method: 'GET'
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to download file' }));
		throw error.detail || 'Failed to download file';
	}

	return await res.blob();
};

/**
 * Stream a file by its ID with chunked responses for better performance
 */
export const streamItemContent = async (
	itemId: string,
	knowledgeId: string,
	chunkSize: number = 1024 * 1024
): Promise<Blob> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}/stream?chunk_size=${chunkSize}`,
		{
			method: 'GET'
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to stream file' }));
		throw error.detail || 'Failed to stream file';
	}

	return await res.blob();
};

/**
 * Get file content with intelligent endpoint selection based on file size
 */
export const getItemContentOptimized = async (
	itemId: string,
	knowledgeId: string,
	fileSize?: number
): Promise<Blob> => {
	// Use streaming for files larger than 10MB
	const largeFileThreshold = 10 * 1024 * 1024; // 10MB

	if (fileSize && fileSize > largeFileThreshold) {
		console.log(`[getItemContentOptimized] Using streaming for large file (${fileSize} bytes)`);
		return streamItemContent(itemId, knowledgeId);
	} else {
		console.log(`[getItemContentOptimized] Using regular download for file`);
		return getItemContentById(itemId, knowledgeId);
	}
};

/**
 * Update an item's content
 */
export const updateItemContent = async (
	knowledgeId: string,
	itemId: string,
	content: string
): Promise<GenericItem> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}`,
		{
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				content: content
			})
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to update item content' }));
		throw error.detail || 'Failed to update item content';
	}

	return res.json();
};

/**
 * Validate file accessibility
 */
export const validateFile = async (itemId: string, knowledgeId: string): Promise<any> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/${itemId}/validate`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to validate file' }));
		throw error.detail || 'Failed to validate file';
	}

	return res.json();
};

/**
 * Clean up orphaned items in a knowledge base
 */
export const cleanupOrphanedItems = async (knowledgeId: string): Promise<any> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/${knowledgeId}/items/cleanup`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to cleanup orphaned items' }));
		throw error.detail || 'Failed to cleanup orphaned items';
	}

	return res.json();
};

/**
 * Clean up failed uploads across all knowledge bases
 */
export const cleanupFailedUploads = async (maxAgeHours: number = 24): Promise<any> => {
	const res = await fetchWithAuth(
		`${WEBUI_API_V2_BASE_URL}/knowledge/items/cleanup/failed-uploads?max_age_hours=${maxAgeHours}`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);

	if (!res.ok) {
		const error = await res.json().catch(() => ({ detail: 'Failed to cleanup failed uploads' }));
		throw error.detail || 'Failed to cleanup failed uploads';
	}

	return res.json();
};
